import { ResourceVideoPlayback } from "@/features/resource-editor/ResourceVideoPlayback";
import type { ResourceVideoSource } from "@/features/resource-editor/resourceVideoSource";
import Konva from "konva";
import { Trash2 } from "lucide-react";
import type { KonvaEventObject } from "konva/lib/Node";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, Layer, Stage, Transformer } from "react-konva";
import {
  type CircleObject,
  type DrawingV2,
  type FormationObject,
  MAX_DRAWING_OBJECTS,
  MAX_TELESTRATOR_LABEL_LENGTH,
  TELESTRATOR_TOOL_KINDS,
  clampTelestratorObject,
  convexHull,
  emptyDrawing,
  type SpotlightObject,
  type TelestratorObject,
} from "@/lib/canvas/telestrator-schema";
import { clampSharedShape, type SharedShape } from "@/lib/canvas/shared-shape";
import { pointToPx, type Dims } from "@/lib/canvas/coords";
import { unitRectFromNodeBox } from "@/lib/canvas/shape-edit";
import {
  isToolAllowed,
  useReviewEditorStore,
  type EditorToolKind,
} from "@/stores/review-editor";
import { ColorPalette } from "./components/ColorPalette";
import { ShapeRenderer } from "./components/ShapeRenderer";
import { StrokeWidthSlider } from "./components/StrokeWidthSlider";
import {
  CircleRenderer,
  DimRenderer,
  FormationRenderer,
  SpotlightRenderer,
} from "./components/TelestratorOverlayRenderers";
import { DrawingStyle } from "./components/DrawingStyle";
import { ToolGrid } from "./components/ToolGrid";
import { HlsVideo } from "@/features/playback/HlsVideo";
import { useVodHeartbeat } from "@/features/playback/useVodHeartbeat";
import { getStreamUrls } from "@/lib/api";
import type { Match } from "@/lib/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CanvasRailControls } from "./components/CanvasRailControls";
import { EndpointHandles } from "./components/EndpointHandles";
import { InlineTextEditor } from "./components/InlineTextEditor";
import { useStageCursors } from "./useStageCursors";
import { useUndoStack } from "@/lib/canvas/useUndoStack";

/**
 * Video-telestrator canvas — Konva stage layered over a `<video>`
 * element with the same drag-state machine as TacticalBoardEditor
 * plus three surface-specific overlay tools (spotlight / dim /
 * formation).
 *
 * Pointer-events policy (issue #105 — PR #6 round-2 doc fix):
 *
 *   The editor surface ALWAYS keeps the overlay listening (CSS
 *   `pointer-events: auto`). Select / drag / delete on existing
 *   overlay objects must work whether the user has a drawing tool
 *   armed or not — an earlier round of this file flipped the
 *   overlay to `pointer-events: none` in select mode, which killed
 *   onSelect / drag / delete for every existing object. That bug is
 *   gone; do NOT re-introduce an implicit toggle keyed on
 *   `armedTool` or `pending`.
 *
 *   Pass-through to native HLS controls is opt-in via the
 *   `inactive` prop:
 *     - inactive=false (editor surface, M3 demo)
 *         → overlay listens. Drawing tools draw. Select clicks
 *           select. Native video controls won't receive clicks
 *           that land on the overlay area, but the M3 demo isn't
 *           a playback context, so this is fine.
 *     - inactive=true (clip-playback mode, M5 feedback player)
 *         → overlay is `pointer-events: none`. The canvas stays
 *           visible (saved drawings remain on top), it just
 *           doesn't intercept input. Native HLS controls receive
 *           every click. Caller may additionally set the canvas
 *           container to `display: none` for full pass-through.
 *
 * Drag-state machine: same as TacticalBoardEditor but the surface
 * scope is "telestrator" — the review-editor store keeps a separate
 * armed-tool / color / stroke per surface.
 *
 * Formation tool: multi-click placement. Click to add anchors (up
 * to 16); press Enter (or the "Commit formation" button in the
 * right rail) when ≥3 anchors are placed to run `convexHull` and
 * persist the FormationObject; Esc cancels and clears the in-flight
 * anchor list.
 */

let _idCounter = 0;
function nextId(prefix: string): string {
  _idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${_idCounter}`;
}

// Ignore sub-4px pointer jitter so a tap on a draggable node stays a
// click (select) instead of becoming a no-op drag that pollutes the
// undo stack. NOTE: `Konva.dragDistance` is app-global (Konva's
// default is 3). Also set by TacticalBoardEditor — idempotent.
Konva.dragDistance = 4;

const TELESTRATOR_TOOLS_AS_EDITOR: readonly EditorToolKind[] =
  TELESTRATOR_TOOL_KINDS as readonly EditorToolKind[];

export type TelestratorCanvasProps = {
  /** Defined (including null) means exact canonical media; no legacy fallback. */
  resourceSource?: ResourceVideoSource|null;
  onResourceUnavailable?: () => void;
  /** Current drawing (canonical v2 in-memory shape). */
  drawing: DrawingV2;
  onDrawingChange: (drawing: DrawingV2) => void;
  /** The match whose video backs the telestrator, + the slot to
   * play. When BOTH are set the canvas mounts `<HlsVideo>` (HLS
   * with MP4 fallback) behind the Konva overlay. When either is
   * absent — the M3 demo route, or a Coach Review session with no
   * match selected yet — it renders a neutral dark placeholder so
   * the canvas still works without a video asset. */
  match?: Pick<Match, "id" | "team_id">;
  slot?: string;
  /** Forwarded to the backing `<HlsVideo>`'s `<video>` element so
   * callers can read `currentTime` / attach listeners (timestamp
   * readout, clip-mode boundary watcher). Called with `null` when
   * no video is mounted. The canvas ALSO holds its own internal
   * ref to the same element (for the VOD heartbeat) — `props.videoRef`
   * and the internal ref are merged onto `<HlsVideo>`.
   *
   * A CALLBACK ref, not a `RefObject`: the element is forwarded up
   * from a callback ref deep inside this component, and Coach
   * Review needs that as a re-render signal (its `useVideoPlayhead`
   * reads the element at render time). A plain `RefObject` write
   * wouldn't re-render the caller. */
  videoRef?: (el: HTMLVideoElement | null) => void;
  /** Called when the VOD heartbeat gets a `403` — the stream was
   * killed by an admin. The canvas pauses the video itself; the
   * caller surfaces the message (toast / inline error). When
   * omitted the heartbeat still runs (keeping the session warm)
   * but a kill is silent beyond the auto-pause. */
  onHeartbeatKilled?: () => void;
  /** Stage width in pixels. Height derives from `aspect`. */
  width: number;
  /** When true, render only the video + stage — no side toolbar.
   * Used by Coach Review focus mode, which renders an external
   * floating tool overlay that drives the same review-editor store.
   * The canvas's own side toolbar would conflict with the overlay. */
  hideToolbar?: boolean;
  /** Extra content appended to the right-rail toolbar, below the
   * built-in tools / color / stroke / formation-mode / clear
   * controls. Coach Review uses this to stack the template
   * selector + composer in the same column as the tools so the
   * coach can type notes while watching the video. Ignored when
   * `hideToolbar` is true. */
  toolbarChildren?: React.ReactNode;
  /** Coach Review v2 — tool-rail relocation slot.
   *
   * When provided, the canvas's ENTIRE toolbar (tool grid / color /
   * stroke / formation+label conditionals / undo / delete / clear)
   * is NOT rendered in the canvas's own right column — instead the
   * canvas calls this with that whole block and the caller places
   * it (the v2 layout portals it into the dedicated right rail).
   * The canvas then renders no own column, so the stage takes the
   * full width. The relocated block drives the SAME `review-editor`
   * store, so the two stay in lockstep.
   *
   * Ignored when `hideToolbar` is true (focus mode owns the tools
   * via its floating overlay). Default (`undefined`) keeps the
   * pre-v2 behavior — the toolbar renders in the canvas column. */
  toolRailSlot?: (toolControls: React.ReactNode) => React.ReactNode;
  /** Aspect ratio of the video (height / width). Defaults to 9/16. */
  aspect?: number;
  /** Playback / "inactive" mode — when true, the canvas stays visible
   * (so saved drawings render over the video) but the entire overlay
   * is `pointer-events: none` so native HLS controls receive every
   * click. The M5 feedback player sets this true in clip-playback
   * mode; the M3 demo always passes false (the editor surface). */
  inactive?: boolean;
  /** Render the drawing layer. Coach Review uses this to keep a saved
   * note's telestration pinned to its marked frame: the drawing state
   * remains intact for editing/saving, but it disappears while the
   * video is playing or parked on a different frame. */
  showDrawing?: boolean;
};

export function TelestratorCanvas(props: TelestratorCanvasProps) {
  const aspect = props.aspect ?? 9 / 16;
  const width = Math.max(1, Math.round(props.width));
  const height = Math.max(1, Math.round(width * aspect));
  const dims: Dims = useMemo(() => ({ width, height }), [width, height]);
  const showDrawing = props.showDrawing ?? true;

  const armedTool = useReviewEditorStore((s) => s.telestrator.armedTool);
  const color = useReviewEditorStore((s) => s.telestrator.color);
  const strokeWidth = useReviewEditorStore((s) => s.telestrator.strokeWidth);
  const selectedId = useReviewEditorStore((s) => s.telestrator.selectedShapeId);
  const pending = useReviewEditorStore((s) => s.telestrator.pendingShape);
  const armTool = useReviewEditorStore((s) => s.armTool);
  const setColor = useReviewEditorStore((s) => s.setColor);
  const setStrokeWidth = useReviewEditorStore((s) => s.setStrokeWidth);
  const setSurface = useReviewEditorStore((s) => s.setSurface);
  const selectShape = useReviewEditorStore((s) => s.selectShape);
  const startPending = useReviewEditorStore((s) => s.startPending);
  const updatePending = useReviewEditorStore((s) => s.updatePending);
  const commitPending = useReviewEditorStore((s) => s.commitPending);
  const cancelPending = useReviewEditorStore((s) => s.cancelPending);
  const disarm = useReviewEditorStore((s) => s.disarm);

  // Pin the store to the telestrator surface on mount.
  useEffect(() => {
    setSurface("telestrator");
  }, [setSurface]);

  // In-progress formation anchors (separate from `pending` because
  // formation is multi-click rather than drag-to-draw).
  const [formationAnchors, setFormationAnchors] = useState<
    { x: number; y: number }[]
  >([]);

  // Label text lives in an anchored tool dropdown, consistent with the
  // drawing-style control and without interrupting the canvas with a modal.
  const [labelText, setLabelText] = useState("");
  const [labelPopoverOpen, setLabelPopoverOpen] = useState(false);
  const [formationPopoverOpen, setFormationPopoverOpen] = useState(false);
  const previousArmedTool = useRef(armedTool);
  useEffect(() => {
    if (previousArmedTool.current !== armedTool) {
      setLabelPopoverOpen(armedTool === "label");
      setFormationPopoverOpen(armedTool === "formation");
      previousArmedTool.current = armedTool;
    }
  }, [armedTool]);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // Direct-manipulation pass: id of the label/zone whose text is
  // being edited via the double-click inline editor.
  const [editingTextId, setEditingTextId] = useState<string | null>(null);

  // M7.5 C1 / C2: Canvas-rail state — visibility toggle + multi-
  // step undo stack (cap 10). Clears on unmount.
  const [canvasVisible, setCanvasVisible] = useState(true);
  const undoStack = useUndoStack<DrawingV2>({ maxDepth: 10 });

  // Direct manipulation: every object is draggable while the editor
  // is active — except under the freehand pen, which must be able
  // to draw over existing shapes.
  const shapesDraggable = armedTool !== "freehand";
  const cursors = useStageCursors({ armedTool });

  /** Wrap every user-initiated write to the drawing so the
   * previous snapshot is pushed onto the undo stack first. Reads and
   * updates `drawingRef` so back-to-back same-task commits compose
   * (e.g. two touch drags lifting in one touchend fire both dragends
   * synchronously — the second must see the first's write). Undo/redo
   * restore via `onDrawingChangeRef` directly so restoring doesn't
   * push onto the stack. */
  const applyDrawing = (next: DrawingV2) => {
    undoStack.push(drawingRef.current);
    drawingRef.current = next;
    props.onDrawingChange(next);
  };

  // M7.5 review-fix P2: the global keydown handler below is
  // registered with a narrow deps array (re-registering on every
  // drawing mutation would thrash the listener). Reading
  // `props.drawing` inside that handler would close over the
  // first-render snapshot — after a drag, resize, or undo, Delete
  // would clobber the canvas with stale state. Mirror the latest
  // drawing into a ref so the handler always reads current state.
  const drawingRef = useRef(props.drawing);
  drawingRef.current = props.drawing;
  const applyDrawingRef = useRef(applyDrawing);
  applyDrawingRef.current = applyDrawing;
  // Undo/redo restore bypasses `applyDrawing` (it must not re-push),
  // so the keydown handler needs its own fresh ref to the setter.
  const onDrawingChangeRef = useRef(props.onDrawingChange);
  onDrawingChangeRef.current = props.onDrawingChange;

  const stageRef = useRef<Konva.Stage | null>(null);
  const transformerRef = useRef<Konva.Transformer | null>(null);

  // The canvas holds its OWN ref to the backing `<video>` (the VOD
  // heartbeat needs it to check paused/ended + pause on a kill),
  // and ALSO forwards `props.videoRef` to the same element for the
  // caller's later needs. `mergedVideoRef` is a callback ref that
  // writes both — see `<HlsVideo ref={mergedVideoRef}>` below.
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const propsVideoRef = props.videoRef;
  const mergedVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      videoElRef.current = el;
      // `props.videoRef` is a callback ref — invoking it gives the
      // caller (Coach Review) its re-render signal.
      propsVideoRef?.(el);
    },
    [propsVideoRef],
  );

  // VOD heartbeat — keeps the streams-registry session warm and is
  // the channel through which an admin "kill" reaches the coach.
  // Only runs when a real video is mounted (match + slot set).
  useVodHeartbeat({
    matchId: props.match?.id,
    slot: props.slot,
    videoRef: videoElRef,
    onKilled: props.onHeartbeatKilled,
    enabled: props.resourceSource === undefined && Boolean(props.match && props.slot),
  });

  // Transformer attach for every box-bounded kind — zone, circle,
  // spotlight (all render as positioned Groups with width/height).
  // Two-point shapes (arrow/line) get EndpointHandles instead; the
  // two mechanisms are disjoint by kind and never co-attach.
  useEffect(() => {
    const stage = stageRef.current;
    const transformer = transformerRef.current;
    if (!stage || !transformer) return;
    // Finish any in-flight resize before re-targeting (see the
    // tactical editor's attach effect for the full rationale) —
    // setNodes() alone leaves partial scale mutations stranded.
    if (transformer.isTransforming()) transformer.stopTransform();
    if (!selectedId) {
      transformer.nodes([]);
      transformer.getLayer()?.batchDraw();
      return;
    }
    const obj = props.drawing.objects.find((o) => o.id === selectedId);
    // While the pen is armed the freehand drawing-gate exception lets
    // a stroke start on ANY node — keep the Transformer detached so
    // an anchor press can't run a resize and a stroke simultaneously.
    if (
      armedTool !== "freehand" &&
      (obj?.kind === "zone" ||
        obj?.kind === "circle" ||
        obj?.kind === "spotlight")
    ) {
      const node = stage.findOne(`#${selectedId}`);
      if (node) transformer.nodes([node]);
      else transformer.nodes([]);
    } else {
      transformer.nodes([]);
    }
    transformer.getLayer()?.batchDraw();
  }, [selectedId, props.drawing.objects, armedTool]);

  // Keyboard shortcuts — same letter map as the tactical board for
  // overlapping tools, plus C/S/D/G for circle/spotlight/dim/
  // formation. `C` = circle ports the legacy telestrator binding.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      // Undo/redo chords — checked before the plain-modifier bail.
      // Read state via refs: this handler deliberately re-registers
      // on a narrow dep list (review-fix P2).
      if ((e.metaKey || e.ctrlKey) && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === "z" && !e.shiftKey) {
          e.preventDefault();
          const prev = undoStack.undo(drawingRef.current);
          if (prev) {
            drawingRef.current = prev;
            onDrawingChangeRef.current(prev);
          }
          return;
        }
        if ((k === "z" && e.shiftKey) || k === "y") {
          e.preventDefault();
          const next = undoStack.redo(drawingRef.current);
          if (next) {
            drawingRef.current = next;
            onDrawingChangeRef.current(next);
          }
          return;
        }
        return; // any other chord belongs to the browser
      }
      if (e.altKey) return;
      const key = e.key.toUpperCase();
      const map: Record<string, EditorToolKind> = {
        V: "select",
        A: "arrow",
        L: "line",
        Z: "zone",
        C: "circle",
        T: "label",
        F: "freehand",
        S: "spotlight",
        D: "dim",
        G: "formation",
      };
      // Coach Review owns the letter-key layer while it is mounted: V/B
      // switch source, F opens Focus, and 1-4 select tools. Letting this
      // embedded editor also process the event creates two state changes for
      // one keypress (for example Focus opens and Freehand arms immediately).
      // Keep the editor's Escape/Delete and modifier-key history handling
      // below/above available to Coach Review.
      if (!document.body.dataset.coachReviewSource && key in map) {
        e.preventDefault();
        armTool(map[key]!);
        setFormationAnchors([]);
        return;
      }
      if (e.key === "Escape") {
        // Step down one level at a time: cancel an in-flight draw /
        // formation first, then clear selection, then disarm back
        // to Select. Read pending imperatively — subscribing to it
        // here would re-register the listener on every mousemove.
        const pendingNow =
          useReviewEditorStore.getState().telestrator.pendingShape;
        if (pendingNow) {
          cancelPending();
          return;
        }
        if (formationAnchors.length > 0) {
          setFormationAnchors([]);
          return;
        }
        if (selectedId) {
          selectShape(null);
          return;
        }
        disarm();
        return;
      }
      if (
        e.key === "Enter" &&
        armedTool === "formation" &&
        formationAnchors.length >= 3
      ) {
        commitFormation();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        // Read via refs (review-fix P2): props.drawing /
        // applyDrawing close over the first-render snapshot;
        // without refs, deleting after a drag / resize / undo
        // would clobber the canvas with stale state.
        const current = drawingRef.current;
        const next = current.objects.filter((o) => o.id !== selectedId);
        if (next.length !== current.objects.length) {
          // M7.5 C2: route delete-via-keyboard through the undo
          // stack so the Undo button can restore it.
          applyDrawingRef.current({ ...current, objects: next });
          selectShape(null);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    armTool,
    cancelPending,
    disarm,
    selectedId,
    selectShape,
    armedTool,
    formationAnchors,
    undoStack,
  ]);

  function unitFromStageEvent(
    e: KonvaEventObject<MouseEvent | TouchEvent>,
  ): { x: number; y: number } | null {
    const stage = e.target.getStage();
    if (!stage) return null;
    const p = stage.getPointerPosition();
    if (!p) return null;
    return { x: clamp01(p.x / dims.width), y: clamp01(p.y / dims.height) };
  }

  function handleMouseDown(e: KonvaEventObject<MouseEvent | TouchEvent>) {
    // Drawing starts only on "background" hits. Beyond the empty
    // stage, the full-frame `frame-catcher` rects (dim, spotlight
    // darkening) count as background while a drawing tool is armed —
    // without this, one placed dim/spotlight swallowed every
    // pointerdown and nothing could ever be drawn again. The
    // freehand pen additionally draws over ANY node (shapes aren't
    // draggable while it's armed).
    const targetNode = e.target as Konva.Node;
    const onStageBackground =
      e.target === e.target.getStage() ||
      (armedTool !== "select" && targetNode.name() === "frame-catcher");
    if (!isToolAllowed(armedTool, "telestrator")) return;
    if (!onStageBackground && armedTool !== "freehand") return;

    const pt = unitFromStageEvent(e);
    if (!pt) return;

    if (armedTool === "select") {
      selectShape(null);
      return;
    }

    // The drawing layer is hidden (showDrawing gate or the rail's
    // Canvas Off toggle) — never start a stroke into an invisible
    // layer. Without this guard, drawing while the layer was hidden
    // (e.g. Coach Review parked away from a note's pinned moment)
    // silently appended invisible objects to the saved drawing.
    // Deliberately AFTER the select branch: a background click must
    // still clear a (now invisible) selection, per the select-always-
    // works policy in the pointer-events block above.
    if (!canvasVisible || !showDrawing) return;

    // Shared shapes — same machine as the tactical board
    if (armedTool === "arrow" || armedTool === "line") {
      const shape: SharedShape = {
        kind: armedTool,
        id: nextId(armedTool),
        x1: pt.x,
        y1: pt.y,
        x2: pt.x,
        y2: pt.y,
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "zone") {
      const shape: SharedShape = {
        kind: "zone",
        id: nextId("zone"),
        x: pt.x,
        y: pt.y,
        w: 0.001,
        h: 0.001,
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "freehand") {
      const shape: SharedShape = {
        kind: "freehand",
        id: nextId("freehand"),
        points: [pt],
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "label") {
      const trimmed = labelText.trim().slice(0, MAX_TELESTRATOR_LABEL_LENGTH);
      if (!trimmed) return;
      const shape: SharedShape = {
        kind: "label",
        id: nextId("label"),
        x: pt.x,
        y: pt.y,
        text: trimmed,
        color,
        strokeWidth,
      };
      const added = commitObject(shape);
      setLabelText("");
      setLabelPopoverOpen(false);
      armTool("select");
      if (added) selectShape(shape.id ?? null);
      return;
    }

    // Telestrator-only overlays
    if (armedTool === "spotlight") {
      const shape: SpotlightObject = {
        kind: "spotlight",
        id: nextId("spotlight"),
        x: pt.x,
        y: pt.y,
        w: 0.001,
        h: 0.001,
        color,
        strokeWidth,
      };
      startPending(shape as unknown as SharedShape);
      return;
    }
    if (armedTool === "circle") {
      const shape: CircleObject = {
        kind: "circle",
        id: nextId("circle"),
        x: pt.x,
        y: pt.y,
        w: 0.001,
        h: 0.001,
        color,
        strokeWidth,
      };
      startPending(shape as unknown as SharedShape);
      return;
    }
    if (armedTool === "dim") {
      const dim = {
        kind: "dim" as const,
        id: nextId("dim"),
        opacity: 0.45,
      };
      const added = commitObject(dim);
      armTool("select");
      if (added) selectShape(dim.id ?? null);
      return;
    }
    if (armedTool === "formation") {
      // Multi-click placement. Accumulate up to 16 anchors; commit
      // with Enter (handled in the keydown effect) when ≥3.
      if (formationAnchors.length >= 16) return;
      setFormationAnchors([...formationAnchors, pt]);
      return;
    }
  }

  function handleMouseMove(e: KonvaEventObject<MouseEvent | TouchEvent>) {
    if (!pending) return;
    const pt = unitFromStageEvent(e);
    if (!pt) return;
    if (pending.kind === "arrow" || pending.kind === "line") {
      updatePending({ ...pending, x2: pt.x, y2: pt.y });
      return;
    }
    if (pending.kind === "zone") {
      const x = Math.min(pending.x, pt.x);
      const y = Math.min(pending.y, pt.y);
      const w = Math.abs(pt.x - pending.x);
      const h = Math.abs(pt.y - pending.y);
      updatePending({ ...pending, x, y, w, h });
      return;
    }
    if (pending.kind === "freehand") {
      const last = pending.points[pending.points.length - 1];
      if (last && Math.abs(last.x - pt.x) + Math.abs(last.y - pt.y) < 0.005) {
        return;
      }
      if (pending.points.length >= 200) {
        commitSharedPending();
        return;
      }
      updatePending({ ...pending, points: [...pending.points, pt] });
      return;
    }
    // Spotlight + circle both extend as a rect-bounded drag from
    // the anchor. They're stored in the pending slot as a
    // SharedShape (cast), so detect via the kind field:
    const pendingKind = (pending as unknown as TelestratorObject).kind;
    if (pendingKind === "spotlight" || pendingKind === "circle") {
      const sp = pending as unknown as SpotlightObject | CircleObject;
      const x = Math.min(sp.x, pt.x);
      const y = Math.min(sp.y, pt.y);
      const w = Math.abs(pt.x - sp.x);
      const h = Math.abs(pt.y - sp.y);
      updatePending({
        ...(pending as unknown as SharedShape),
        // Cast-back tracks all the same fields on the on-store record
        x,
        y,
        w,
        h,
      } as unknown as SharedShape);
      return;
    }
  }

  function handleMouseUp() {
    if (!pending) return;
    const kind = (pending as unknown as TelestratorObject).kind;
    if (kind === "spotlight" || kind === "circle") {
      commitBoundedPending();
    } else {
      commitSharedPending();
    }
  }

  function commitSharedPending() {
    const committed = commitPending();
    if (!committed) return;
    const final = clampSharedShape(committed);
    // Reject degenerate shapes:
    if (final.kind === "zone" && (final.w < 0.02 || final.h < 0.02)) return;
    if (final.kind === "freehand" && final.points.length < 2) return;
    if (
      (final.kind === "arrow" || final.kind === "line") &&
      Math.abs(final.x1 - final.x2) < 0.005 &&
      Math.abs(final.y1 - final.y2) < 0.005
    ) {
      return;
    }
    // Auto-select the freshly drawn shape so it can be moved /
    // endpoint-adjusted immediately (the tool stays armed for rapid
    // multi-draw; drawing still starts on background hits).
    if (commitObject(final)) selectShape(final.id ?? null);
  }

  /** Commit a rect-bounded telestrator overlay (spotlight OR
   * circle) — both drag out the same `(x, y, w, h)` box and reject
   * a degenerate sub-0.02 result. */
  function commitBoundedPending() {
    const committed = commitPending();
    if (!committed) return;
    const sp = committed as unknown as SpotlightObject | CircleObject;
    if (sp.w < 0.02 || sp.h < 0.02) return;
    const final = clampTelestratorObject(sp);
    if (commitObject(final)) selectShape(final.id ?? null);
  }

  function commitFormation() {
    if (formationAnchors.length < 3) return;
    const anchors = formationAnchors.slice(0, 16).map((p) => ({
      x: clamp01(p.x),
      y: clamp01(p.y),
    }));
    const hull = convexHull(anchors);
    const formation: FormationObject = {
      kind: "formation",
      id: nextId("formation"),
      anchors,
      hull_points: hull,
      color,
      strokeWidth,
    };
    const added = commitObject(formation);
    setFormationAnchors([]);
    armTool("select");
    if (added) selectShape(formation.id ?? null);
  }

  /** Append an object; returns false when the MAX_DRAWING_OBJECTS
   * (120) cap rejected it — the backend 422s a drawing with more.
   * Silent no-op at the limit, consistent with the degenerate-shape
   * rejections in the commit paths above; callers use the return to
   * skip auto-selecting an object that was never added. */
  function commitObject(obj: TelestratorObject): boolean {
    const current = drawingRef.current;
    if (current.objects.length >= MAX_DRAWING_OBJECTS) return false;
    applyDrawing({
      ...current,
      objects: [...current.objects, obj],
    });
    return true;
  }

  function replaceObject(updated: TelestratorObject) {
    const final = clampTelestratorObject(updated);
    const current = drawingRef.current;
    applyDrawing({
      ...current,
      objects: current.objects.map((o) => (o.id === final.id ? final : o)),
    });
  }

  function handleTransformEnd(e: KonvaEventObject<Event>) {
    const node = e.target as Konva.Node | undefined;
    // A transform aborted after the Transformer was emptied fires
    // transformend with no target node.
    if (!node || typeof node.id !== "function") return;
    const id = node.id();
    const obj = props.drawing.objects.find((o) => o.id === id);
    if (
      !obj ||
      (obj.kind !== "zone" && obj.kind !== "circle" && obj.kind !== "spotlight")
    ) {
      return;
    }
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    const rect = unitRectFromNodeBox(
      {
        x: node.x(),
        y: node.y(),
        width: node.width() * scaleX,
        height: node.height() * scaleY,
      },
      dims,
    );
    replaceObject({ ...obj, ...rect });
  }

  /** Commit the inline (double-click) text editor. Labels require
   * text, so an empty commit is a no-op; zone captions clear. */
  function commitTextEdit(id: string, raw: string) {
    setEditingTextId(null);
    const obj = props.drawing.objects.find((o) => o.id === id);
    if (!obj) return;
    if (obj.kind === "label") {
      const text = raw.trim().slice(0, MAX_TELESTRATOR_LABEL_LENGTH);
      if (!text || text === obj.text) return;
      replaceObject({ ...obj, text });
      return;
    }
    if (obj.kind === "zone") {
      const text = raw.trim().slice(0, 80);
      if ((text || undefined) === obj.text) return;
      replaceObject({ ...obj, text: text || undefined });
    }
  }

  // Pointer-events flip (issue #105).
  //
  // The legacy painter at `js/coaching/review.js:1057-1066` toggles
  // `canvas.style.pointerEvents` between 'auto' (drawing active) and
  // 'none' (drawing passive). The intent of 'none' was clip-playback
  // mode where the user wants to press Play on the video and the
  // canvas would otherwise swallow that click.
  //
  // BUT — in editor mode, select needs to keep pointer events on so
  // the user can click an existing object to select / drag / delete
  // it. PR #6 review caught that the previous condition
  // (`stageBlocksPointer = armedTool !== "select" || pending !== null`)
  // killed every select interaction.
  //
  // Correct model:
  //   - inactive=true (clip-playback / read-only) → ALWAYS none.
  //     Saved drawings render but the canvas is invisible to input.
  //   - inactive=false (editor) → ALWAYS auto. The drag-state
  //     machine inside the stage decides what each click does;
  //     pass-through to video controls is opt-in via the inactive
  //     prop, not implicit on tool state.
  //
  // The M3 demo always passes inactive=false. The M5 feedback player
  // will set inactive=true in clip-playback mode.
  const stageBlocksPointer = !props.inactive;

  return (
    <div
      className="flex flex-col lg:flex-row gap-4 w-full"
      data-coach-review-source="telestrator"
    >
      <div
        className="relative flex-shrink-0 rounded overflow-hidden border border-rule bg-black"
        style={{ width, height }}
        data-testid="telestrator-stage-container"
      >
        {props.resourceSource !== undefined ? (
          props.resourceSource ? <ResourceVideoPlayback source={props.resourceSource} videoRef={mergedVideoRef}
            controls={props.inactive??false} onUnavailable={props.onResourceUnavailable}/>
            : <div className="absolute inset-0 flex items-center justify-center text-paper/75" role="status">Choose a recording to begin.</div>
        ) : props.match && props.slot ? (
          (() => {
            const { hlsUrl, mp4Url } = getStreamUrls(props.match, props.slot);
            return (
              <HlsVideo
                ref={mergedVideoRef}
                hlsUrl={hlsUrl}
                mp4Url={mp4Url}
                // Native `<video>` controls ONLY in playback
                // (`inactive`) mode — the M5 feedback player, where
                // the overlay is `pointer-events: none` so clicks
                // reach the scrubber. On the editor surface (Coach
                // Review, the demo) the Konva overlay sits on top so
                // the native controls are unreachable dead chrome —
                // Coach Review drives playback through its own
                // `<FrameStepper>` + `<ClipTrack>` instead.
                controls={props.inactive ?? false}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="telestrator-video"
              />
            );
          })()
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center text-paper/75"
            data-testid="telestrator-no-video"
          >
            <span className="text-sm font-mono">
              No match selected — pick a match to load video
            </span>
          </div>
        )}
        <div
          className="absolute inset-0"
          style={{
            pointerEvents: stageBlocksPointer ? "auto" : "none",
            touchAction: stageBlocksPointer ? "none" : "auto",
            cursor: cursors.baseCursor,
          }}
        >
          <Stage
            ref={stageRef}
            width={width}
            height={height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
            onMouseOver={cursors.onMouseOver}
            onMouseOut={cursors.onMouseOut}
          >
            {/* M7.5 C1: hide the drawing layer when Canvas is "Off". */}
            <Layer visible={canvasVisible && showDrawing}>
              {props.drawing.objects.map((obj, i) => {
                const key = obj.id ?? `obj-${i}`;
                // The pen draws over shapes; a click mustn't hijack
                // selection mid-stroke.
                const select = () => {
                  if (armedTool === "freehand") return;
                  selectShape(obj.id ?? null);
                };
                switch (obj.kind) {
                  case "spotlight":
                    return (
                      <SpotlightRenderer
                        key={key}
                        shape={obj}
                        dims={dims}
                        selected={obj.id === selectedId}
                        onSelect={select}
                        // The full-frame darkening selects only under
                        // the Select tool — with a drawing tool armed
                        // it counts as background (frame-catcher).
                        onFrameSelect={
                          armedTool === "select" ? select : undefined
                        }
                        draggable={shapesDraggable}
                        onDragEnd={(s) => replaceObject(s)}
                      />
                    );
                  case "circle":
                    return (
                      <CircleRenderer
                        key={key}
                        shape={obj}
                        dims={dims}
                        selected={obj.id === selectedId}
                        onSelect={select}
                        draggable={shapesDraggable}
                        onDragEnd={(s) => replaceObject(s)}
                      />
                    );
                  case "dim":
                    return (
                      <DimRenderer
                        key={key}
                        shape={obj}
                        dims={dims}
                        selected={obj.id === selectedId}
                        // The dim IS a full-frame catcher — selecting
                        // it requires the Select tool so drawing over
                        // it keeps working.
                        onSelect={armedTool === "select" ? select : undefined}
                      />
                    );
                  case "formation":
                    return (
                      <FormationRenderer
                        key={key}
                        shape={obj}
                        dims={dims}
                        selected={obj.id === selectedId}
                        onSelect={select}
                        draggable={shapesDraggable}
                        onDragEnd={(s) => replaceObject(s)}
                      />
                    );
                  default:
                    return (
                      <ShapeRenderer
                        key={key}
                        shape={obj}
                        dims={dims}
                        selected={obj.id === selectedId}
                        onSelect={select}
                        draggable={shapesDraggable}
                        onDragEnd={(s) => replaceObject(s)}
                        onTextEdit={
                          obj.kind === "label" || obj.kind === "zone"
                            ? () => setEditingTextId(obj.id ?? null)
                            : undefined
                        }
                      />
                    );
                }
              })}
              {/* Pending (mid-drag) shape preview. `pending` holds
               * shared shapes directly, but circle/spotlight are
               * cast into the slot — dispatch on `kind` so the
               * bounded overlays draw a live preview as the coach
               * drags, instead of only appearing on mouse-up. */}
              {pending
                ? (() => {
                    const pk = (pending as unknown as TelestratorObject).kind;
                    if (pk === "circle") {
                      return (
                        <CircleRenderer
                          shape={pending as unknown as CircleObject}
                          dims={dims}
                        />
                      );
                    }
                    if (pk === "spotlight") {
                      return (
                        <SpotlightRenderer
                          shape={pending as unknown as SpotlightObject}
                          dims={dims}
                        />
                      );
                    }
                    return <ShapeRenderer shape={pending} dims={dims} />;
                  })()
                : null}
              {/* Formation tool — in-progress anchor dots. The
               * coach clicks to drop anchors; each shows as a dot
               * in the selected color so placement is visible
               * before "Commit formation" runs `convexHull`. */}
              {armedTool === "formation"
                ? formationAnchors.map((p, i) => (
                    <Circle
                      key={`formation-anchor-${i}`}
                      x={p.x * dims.width}
                      y={p.y * dims.height}
                      radius={6}
                      fill={color}
                      stroke="rgba(15, 23, 42, 0.85)"
                      strokeWidth={1.5}
                      listening={false}
                    />
                  ))
                : null}
              <Transformer
                ref={transformerRef}
                rotateEnabled={false}
                boundBoxFunc={(oldBox, newBox) => {
                  if (newBox.width < 8 || newBox.height < 8) return oldBox;
                  return newBox;
                }}
                onTransformEnd={handleTransformEnd}
              />
              {/* Endpoint anchors for the selected arrow/line — last
               * children of the layer so they sit on top of the hit
               * graph and beat the underlying draggable line. */}
              {(() => {
                // Hidden while the pen is armed — the freehand gate
                // exception would otherwise start a stroke AND a
                // handle drag from one pointerdown on an anchor.
                if (armedTool === "freehand") return null;
                const sel = props.drawing.objects.find(
                  (o) => o.id === selectedId,
                );
                if (!sel || (sel.kind !== "arrow" && sel.kind !== "line")) {
                  return null;
                }
                return (
                  <EndpointHandles
                    shape={sel}
                    dims={dims}
                    onCommit={(s) => replaceObject(s)}
                  />
                );
              })()}
            </Layer>
          </Stage>
          {/* Double-click inline text editor for labels / zone
           * captions — an HTML input overlaid on the stage. */}
          {(() => {
            if (!editingTextId) return null;
            const obj = props.drawing.objects.find(
              (o) => o.id === editingTextId,
            );
            if (!obj || (obj.kind !== "label" && obj.kind !== "zone")) {
              return null;
            }
            const p = pointToPx({ x: obj.x, y: obj.y }, dims);
            return (
              <InlineTextEditor
                // Clamp inside the overflow-hidden stage box so an
                // edge-shape's editor isn't clipped out of view
                // (input is w-44 = 176px + padding).
                x={Math.max(0, Math.min(p.x, dims.width - 184))}
                y={Math.max(0, Math.min(p.y, dims.height - 40))}
                initialValue={
                  obj.kind === "label" ? obj.text : (obj.text ?? "")
                }
                maxLength={
                  obj.kind === "label" ? MAX_TELESTRATOR_LABEL_LENGTH : 80
                }
                placeholder={
                  obj.kind === "zone" ? "Zone caption" : "Label text"
                }
                onCommit={(text) => commitTextEdit(editingTextId, text)}
                onCancel={() => setEditingTextId(null)}
                data-testid="telestrator-inline-text-editor"
              />
            );
          })()}
        </div>
      </div>

      {props.hideToolbar
        ? null
        : (() => {
            // The ENTIRE rail body — tools + formation/label
            // conditionals + canvas controls + clear. The v2
            // `toolRailSlot` relocates the whole block into the Coach
            // Review right rail so the canvas surface stays
            // chrome-free; without a slot it renders in the canvas's
            // own `w-[300px]` column exactly as before.
            const railContent = (
              <>
                <ToolGrid
                  horizontal={Boolean(props.toolRailSlot)}
                  tools={TELESTRATOR_TOOLS_AS_EDITOR}
                  value={armedTool}
                  onChange={(t) => {
                    armTool(t);
                    setLabelPopoverOpen(t === "label");
                    setFormationPopoverOpen(t === "formation");
                    setFormationAnchors([]);
                  }}
                  toolPopovers={{
                    ...(armedTool === "label" && labelPopoverOpen
                      ? {
                          label: (
                            <label className="canvas-label-settings">
                              <span>Label</span>
                              <input
                                autoFocus
                                type="text"
                                value={labelText}
                                onChange={(event) =>
                                  setLabelText(event.target.value)
                                }
                                maxLength={MAX_TELESTRATOR_LABEL_LENGTH}
                                aria-label="Label"
                              />
                              <small>Enter text, then tap the canvas.</small>
                            </label>
                          ),
                        }
                      : {}),
                    ...(armedTool === "formation" && formationPopoverOpen
                      ? {
                          formation: (
                            <div className="canvas-formation-settings">
                              <span>Formation</span>
                              <p>
                                Tap at least three points around the group, then
                                commit the formation.
                              </p>
                              <button
                                type="button"
                                onClick={commitFormation}
                                disabled={formationAnchors.length < 3}
                              >
                                Commit formation ({formationAnchors.length}/3)
                              </button>
                            </div>
                          ),
                        }
                      : {}),
                  }}
                />
                {props.toolRailSlot ? (
                  <DrawingStyle
                    onOpen={() => {
                      setLabelPopoverOpen(false);
                      setFormationPopoverOpen(false);
                    }}
                  >
                    <ColorPalette
                      approved={Boolean(props.toolRailSlot)}
                      value={color}
                      onChange={setColor}
                    />
                    <StrokeWidthSlider
                      value={strokeWidth}
                      onChange={setStrokeWidth}
                      label="Stroke width"
                    />
                  </DrawingStyle>
                ) : (
                  <>
                    <ColorPalette
                      approved={Boolean(props.toolRailSlot)}
                      value={color}
                      onChange={setColor}
                    />
                    <StrokeWidthSlider
                      value={strokeWidth}
                      onChange={setStrokeWidth}
                    />
                  </>
                )}
                {/* M7.5 C1 / C2 / C3: Canvas-rail controls. */}
                <CanvasRailControls
                  compact={Boolean(props.toolRailSlot)}
                  visible={canvasVisible}
                  onToggleVisible={() => setCanvasVisible((v) => !v)}
                  undoDepth={undoStack.depth}
                  onUndo={() => {
                    const prev = undoStack.undo(drawingRef.current);
                    if (prev) {
                      drawingRef.current = prev;
                      onDrawingChangeRef.current(prev);
                    }
                  }}
                  redoDepth={undoStack.redoDepth}
                  onRedo={() => {
                    const next = undoStack.redo(drawingRef.current);
                    if (next) {
                      drawingRef.current = next;
                      onDrawingChangeRef.current(next);
                    }
                  }}
                  hasSelection={selectedId != null}
                  onDeleteSelected={() => {
                    if (!selectedId) return;
                    const current = drawingRef.current;
                    const next = current.objects.filter(
                      (o) => o.id !== selectedId,
                    );
                    if (next.length === current.objects.length) return;
                    applyDrawing({ ...current, objects: next });
                    selectShape(null);
                  }}
                  testidPrefix="telestrator-rail"
                />

                <div className="flex flex-col gap-2 mt-auto">
                  {/* M7.5 B4: styled confirm replaces `window.confirm`. */}
                  <button
                    type="button"
                    onClick={() => setClearConfirmOpen(true)}
                    aria-label="Clear drawing"
                    title="Clear drawing"
                    data-canvas-clear-drawing
                    className="border border-destructive/40 text-destructive hover:bg-destructive/10 rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
                  >
                    {props.toolRailSlot ? (
                      <>
                        <Trash2 size={18} aria-hidden />
                        <span className="canvas-compact-label">
                          Clear drawing
                        </span>
                      </>
                    ) : (
                      "Clear drawing"
                    )}
                  </button>
                </div>
                {props.toolbarChildren ? (
                  <div className="flex flex-col gap-4 pt-4 border-t border-rule/40">
                    {props.toolbarChildren}
                  </div>
                ) : null}
              </>
            );
            // With a `toolRailSlot`, hand the whole rail to the
            // caller and render NO own column — the canvas surface
            // takes the full width. Otherwise render the canvas's
            // own `w-[300px]` rail column.
            return props.toolRailSlot ? (
              props.toolRailSlot(railContent)
            ) : (
              <div
                className="flex max-h-[55dvh] w-full flex-col gap-4 overflow-y-auto rounded-panel border border-rule bg-surface p-3 lg:max-h-[var(--tel-stage-h)] lg:w-[300px] lg:flex-shrink-0 lg:border-0 lg:bg-transparent lg:p-0"
                style={
                  { "--tel-stage-h": `${height}px` } as React.CSSProperties
                }
                data-testid="canvas-toolbar-rail"
              >
                {railContent}
              </div>
            );
          })()}

      {/* M7.5 B4: styled clear-drawing confirm. Routes through
       * `applyDrawing` so the cleared state is pushed onto the undo
       * stack — Undo restores the previous drawing. */}
      {clearConfirmOpen ? (
        <ConfirmDialog
          title="Clear all telestration?"
          message="This removes every object on the canvas. Undo to restore."
          confirmLabel="Clear drawing"
          danger
          onConfirm={() => {
            applyDrawing(emptyDrawing());
            selectShape(null);
            setFormationAnchors([]);
            setClearConfirmOpen(false);
          }}
          onClose={() => setClearConfirmOpen(false)}
          data-testid="telestrator-clear-confirm"
        />
      ) : null}
    </div>
  );
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
